/**
 * Welcome offer popup — general site-wide signup incentive.
 *
 * Trigger: shows once per browser session (sessionStorage), 5s after load,
 * to any visitor who hasn't already signed up or previously dismissed it.
 * Both signing up and closing the popup (✕ button or overlay click) set a
 * permanent localStorage flag — once either happens, it never shows again
 * on that browser, not just for the rest of the session.
 *
 * Submit posts straight to Klaviyo's legacy list-subscribe endpoint (same
 * pattern as assets/email-gate.js — the modern Client Subscribe API is
 * CORS-blocked from inside Shopify, this legacy one is a "simple request"
 * with no preflight and a readable JSON response). This only captures the
 * email; the discount code shown on success is a static, evergreen code set
 * in the section schema (`discount_code`), not generated per-customer.
 */
(function () {
  'use strict';

  var STORAGE_SIGNED_UP = 'welcome_offer_signed_up';
  var STORAGE_DISMISSED = 'welcome_offer_dismissed';
  var SESSION_SHOWN = 'welcome_offer_shown_session';
  var KLAVIYO_SOURCE = 'Welcome Popup';
  var SHOW_DELAY_MS = 5000;

  function localStorageAvailable() {
    try {
      window.localStorage.setItem('__welcome_offer_test__', '1');
      window.localStorage.removeItem('__welcome_offer_test__');
      return true;
    } catch (e) {
      return false;
    }
  }

  function sessionStorageAvailable() {
    try {
      window.sessionStorage.setItem('__welcome_offer_test__', '1');
      window.sessionStorage.removeItem('__welcome_offer_test__');
      return true;
    } catch (e) {
      return false;
    }
  }

  var canUseLocalStorage = localStorageAvailable();
  var canUseSessionStorage = sessionStorageAvailable();

  function getFlag(key) {
    return canUseLocalStorage && window.localStorage.getItem(key) === '1';
  }

  function setFlag(key) {
    if (canUseLocalStorage) window.localStorage.setItem(key, '1');
  }

  function showError(errorEl, message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function init() {
    var modal = document.querySelector('[data-welcome-offer-popup]');
    if (!modal) return;

    var listId = modal.dataset.klaviyoListId;

    var signedUp = getFlag(STORAGE_SIGNED_UP);
    var dismissed = getFlag(STORAGE_DISMISSED);
    var shownThisSession = canUseSessionStorage && window.sessionStorage.getItem(SESSION_SHOWN) === '1';

    // Without sessionStorage (privacy mode, etc.) we can't remember it was
    // already shown this session — better to show once than not at all.
    var shouldShow = !signedUp && !dismissed && !shownThisSession;

    if (!shouldShow) return;

    if (canUseSessionStorage) {
      window.sessionStorage.setItem(SESSION_SHOWN, '1');
    }

    var closeBtn = modal.querySelector('.drawer__close-btn');
    var overlayEl = modal.querySelector('.fixed-overlay');
    [closeBtn, overlayEl].forEach(function (el) {
      if (el) el.addEventListener('click', function () { setFlag(STORAGE_DISMISSED); });
    });

    var emailInput = modal.querySelector('input[name="email"]');

    setTimeout(function () {
      if (typeof modal.show !== 'function') return;
      modal.show();
      // The modal's own focus-trap lands on the first <button> (the close
      // icon) by default — for a signup form the email field is the useful
      // place to land, and it avoids the close button visibly showing the
      // browser's default focus ring the instant the popup opens.
      if (emailInput) emailInput.focus();
    }, SHOW_DELAY_MS);

    var form = modal.querySelector('[data-welcome-offer-form]');
    if (!form) return;

    var errorEl = modal.querySelector('[data-welcome-offer-error]');
    var formState = modal.querySelector('[data-welcome-offer-form-state]');
    var successState = modal.querySelector('[data-welcome-offer-success]');
    var submitBtn = form.querySelector('button[type="submit"]');

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (errorEl) errorEl.hidden = true;

      if (getFlag(STORAGE_SIGNED_UP)) {
        showError(errorEl, modal.dataset.alreadyRegisteredError || "You've already claimed this offer.");
        return;
      }

      var emailInput = form.querySelector('input[name="email"]');
      var email = emailInput ? emailInput.value.trim() : '';

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showError(errorEl, modal.dataset.invalidEmailError || 'Please enter a valid email address.');
        return;
      }

      if (submitBtn) submitBtn.disabled = true;

      var body = new URLSearchParams();
      body.set('g', listId);
      body.set('email', email);
      body.set('$fields', '$source');
      body.set('$source', KLAVIYO_SOURCE);

      fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          return res.text().then(function (rawBody) {
            var payload = null;
            try { payload = JSON.parse(rawBody); } catch (e) { /* non-JSON body */ }

            var hasErrors = payload && Array.isArray(payload.errors) && payload.errors.length > 0;
            var explicitFailure = payload && payload.success === false;

            if (!res.ok || hasErrors || explicitFailure) {
              var message = (payload && payload.errors && payload.errors[0]) ||
                (payload && payload.message) ||
                modal.dataset.genericError ||
                'Something went wrong — please try again.';
              throw new Error(message);
            }

            setFlag(STORAGE_SIGNED_UP);
            if (formState) formState.hidden = true;
            if (successState) successState.hidden = false;
          });
        })
        .catch(function (err) {
          showError(errorEl, err.message || modal.dataset.genericError || 'Something went wrong — please try again.');
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    });

    var copyBtn = modal.querySelector('[data-welcome-offer-copy]');
    var copyBtnLabel = copyBtn && copyBtn.querySelector('[data-welcome-offer-copy-label]');
    if (copyBtn && copyBtnLabel) {
      var copyBtnDefaultLabel = copyBtnLabel.textContent;
      copyBtn.addEventListener('click', function () {
        var code = copyBtn.dataset.code || '';
        if (!code || !navigator.clipboard) return;

        navigator.clipboard.writeText(code).then(function () {
          copyBtnLabel.textContent = modal.dataset.copiedLabel || 'Copied!';
          setTimeout(function () {
            copyBtnLabel.textContent = copyBtnDefaultLabel;
          }, 2000);
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
